/**
 * Shared refresh mechanics for both SPAs (customer app in `src/`, management app
 * in `src-management/`). They run on the SAME origin, so their refresh attempts
 * must coordinate rather than race.
 *
 * Three rules live here:
 *
 * 1. **Cookie is the credential.** The browser never sends a refresh token in
 *    the body; the httpOnly cookie is the single authoritative copy per browser
 *    (API-SPECIFICATION §1.2). A stale copy in another tab can no longer beat
 *    the fresh cookie and trip server-side reuse detection.
 * 2. **Failures are classified, not conflated.** Only a 401 means "this session
 *    is over". Network errors, 5xx and 429 are transient: retry with backoff and
 *    keep the session. Previously any non-2xx logged the operator out, so a
 *    deploy restart, a Redis blip or a rate-limit spike ended the session.
 * 3. **One refresh per browser at a time.** A Web Lock serializes refreshes
 *    across tabs and across both SPAs; the winner broadcasts the new access
 *    token so the waiters adopt it instead of issuing their own refresh.
 */

export type ClientApp = "web" | "management";

export const CLIENT_APP_HEADER = "X-Client-App";

/** One lock + one channel for the whole origin, so both SPAs coordinate. */
const REFRESH_LOCK_NAME = "oktogo-auth-refresh";
const REFRESH_CHANNEL_NAME = "oktogo-auth";

const TRANSIENT_RETRY_DELAYS_MS = [1_000, 3_000, 8_000];
const MAX_RETRY_AFTER_MS = 30_000;

export type RefreshOutcome =
  | { kind: "success"; accessToken: string; expiresIn: number }
  /** Credential is definitively gone: revoked, reused, expired. Terminate. */
  | { kind: "invalid" }
  /** Server or network hiccup. Session is probably fine — retry. */
  | { kind: "transient"; retryAfterMs?: number };

export type RefreshBroadcast = {
  type: "token-refreshed";
  app: ClientApp;
  accessToken: string;
  expiresIn: number;
};

function jitter(ms: number): number {
  return Math.round(ms * (0.75 + Math.random() * 0.5));
}

/** Decode the `userId` claim without verifying — used only to detect identity drift. */
export function decodeAccessTokenUserId(accessToken: string): string | null {
  try {
    const segment = accessToken.split(".")[1];
    if (!segment) return null;
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64)) as { userId?: unknown };
    return typeof payload.userId === "string" ? payload.userId : null;
  } catch {
    return null;
  }
}

function parseRetryAfterMs(res: Response): number | undefined {
  const header = res.headers?.get?.("Retry-After");
  if (!header) return undefined;
  const seconds = Number(header);
  if (!Number.isFinite(seconds) || seconds <= 0) return undefined;
  return Math.min(seconds * 1000, MAX_RETRY_AFTER_MS);
}

/**
 * Perform one refresh call. Cookie-only: no refresh token is read from or
 * written to JavaScript-reachable storage.
 */
export async function performRefreshRequest(
  baseUrl: string,
  app: ClientApp,
  /**
   * One-shot migration only: a refresh token left in web storage by a session
   * created before the cookie became authoritative. The server prefers the
   * cookie for browser clients, so this is used solely when no cookie exists
   * and cannot resurrect the stale-copy race.
   */
  legacyRefreshToken?: string | null,
): Promise<RefreshOutcome> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json", [CLIENT_APP_HEADER]: app },
      body: JSON.stringify(legacyRefreshToken ? { refreshToken: legacyRefreshToken } : {}),
      credentials: "include",
    });
  } catch {
    // Offline, DNS, connection reset, deploy restart mid-flight.
    return { kind: "transient" };
  }

  if (res.status === 401 || res.status === 403) return { kind: "invalid" };
  if (!res.ok) return { kind: "transient", retryAfterMs: parseRetryAfterMs(res) };

  try {
    const data = await res.json();
    const accessToken = data?.data?.accessToken;
    if (data?.success && typeof accessToken === "string" && accessToken) {
      const rawExpiresIn = data.data.expiresIn;
      const expiresIn =
        typeof rawExpiresIn === "number" && rawExpiresIn > 0 ? rawExpiresIn : 900;
      return { kind: "success", accessToken, expiresIn };
    }
  } catch {
    return { kind: "transient" };
  }
  // 2xx without a usable token: treat as transient rather than killing the session.
  return { kind: "transient" };
}

/**
 * Retry transient failures with bounded exponential backoff. Returns the final
 * outcome; `invalid` short-circuits immediately.
 */
export async function refreshWithBackoff(
  baseUrl: string,
  app: ClientApp,
  options: {
    maxAttempts?: number;
    sleep?: (ms: number) => Promise<void>;
    legacyRefreshToken?: string | null;
  } = {},
): Promise<RefreshOutcome> {
  const maxAttempts = options.maxAttempts ?? TRANSIENT_RETRY_DELAYS_MS.length + 1;
  const sleep =
    options.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));

  let outcome: RefreshOutcome = { kind: "transient" };
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    // Only the first attempt may present the legacy token: once it has been sent
    // it is either spent (rotated) or invalid, and re-presenting a rotated token
    // is exactly what reuse detection punishes.
    outcome = await performRefreshRequest(
      baseUrl,
      app,
      attempt === 0 ? options.legacyRefreshToken : null,
    );
    if (outcome.kind !== "transient") return outcome;
    if (attempt === maxAttempts - 1) break;
    const base =
      outcome.retryAfterMs ??
      TRANSIENT_RETRY_DELAYS_MS[Math.min(attempt, TRANSIENT_RETRY_DELAYS_MS.length - 1)];
    await sleep(jitter(base));
  }
  return outcome;
}

type LockManagerLike = {
  request: (name: string, callback: () => Promise<unknown>) => Promise<unknown>;
};

function getLockManager(): LockManagerLike | null {
  if (typeof navigator === "undefined") return null;
  const locks = (navigator as Navigator & { locks?: LockManagerLike }).locks;
  return locks && typeof locks.request === "function" ? locks : null;
}

/**
 * Run `fn` under a cross-tab exclusive lock when the browser supports Web Locks.
 * Without support (older Safari, jsdom) we still run — the server-side rotation
 * grace window absorbs the resulting overlap.
 */
export async function runWithCrossTabLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = getLockManager();
  if (!locks) return fn();
  try {
    return (await locks.request(REFRESH_LOCK_NAME, fn)) as T;
  } catch {
    return fn();
  }
}

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    return new BroadcastChannel(REFRESH_CHANNEL_NAME);
  } catch {
    return null;
  }
}

/** Tell the other tabs (and the other SPA) about a freshly minted access token. */
export function publishRefreshResult(message: RefreshBroadcast): void {
  const channel = getChannel();
  if (!channel) return;
  try {
    channel.postMessage(message);
  } finally {
    channel.close();
  }
}

/** Subscribe to tokens minted by other tabs. Returns an unsubscribe function. */
export function subscribeRefreshResults(
  app: ClientApp,
  handler: (message: RefreshBroadcast) => void,
): () => void {
  const channel = getChannel();
  if (!channel) return () => {};
  const listener = (event: MessageEvent) => {
    const data = event.data as RefreshBroadcast | undefined;
    if (!data || data.type !== "token-refreshed") return;
    // Both SPAs share the channel but not the session: ignore the other app's tokens.
    if (data.app !== app) return;
    if (typeof data.accessToken !== "string" || !data.accessToken) return;
    handler(data);
  };
  channel.addEventListener("message", listener);
  return () => {
    channel.removeEventListener("message", listener);
    channel.close();
  };
}
